import {
    ClearinghousePayerApi,
    CoreFetch,
    createRequestFilters,
    ICustomFilter,
    ListProviderPayersOrderEnum,
    PayerApi,
    ProviderClearinghousePayer,
    ProviderCreatePayerPayload,
    ProviderPayer,
    ProviderPayerSortType,
    tabCountFromHeader,
} from '@enter/core';
import { action, makeObservable, observable, runInAction } from 'mobx';
import type { IFeatures } from './root.store';
import type { PayerRoutingGenerator } from './payer-routing.generator';

/**
 * The payers a provider's claims go to, and where each kind of data actually goes.
 *
 * Separate from `PayersStore`, which lists `ProviderPayerProvider` — payer↔provider
 * ENROLMENT. That answers "is this provider enrolled with this payer"; this answers "where do
 * my claims, appeals and paperwork end up". Different question, different entity.
 *
 * Read-only apart from creating a payer. @John Russell, 2026-08-25: *"We don't want to surface
 * editing payers to the provider page right now… we just want to surface a read-only view of
 * this for providers."* Providers submit a payer, ENTER reviews and configures it — and
 * `createPayer` is that submit (FE-79, asked for by Jordan Kelley 2026-09-11). The provider API
 * has no update, disable or delete, so nothing else here writes.
 *
 * ## Unknown is not none
 *
 * A payer's EDI number and supported transactions live on `ProviderClearinghousePayer`, not on
 * the payer, so the routing view needs a client-side join. Until that join resolves for a
 * payer we do not know how its data is routed — and rendering "drops to paper" for a payer we
 * simply have not asked about yet is a false statement about where real claims go (NS-3).
 *
 * So `getBindings` returns `undefined` for "not loaded" and `[]` only for a payer we asked
 * about and that genuinely has no connection. Callers must render those differently.
 */

/** Keeps the query string clear of URL length limits at any realistic page size. */
const MAX_IDS_PER_REQUEST = 25;

export interface IPayerRoutingListingRequest {
    page?: number;
    pageSize?: number;
    filters?: ICustomFilter[];
    sort?: ProviderPayerSortType[];
    /**
     * Plain strings, not the enum, because that is what `parseSortBy` hands back — and a TS
     * enum is nominal even when its values are the identical strings. Mapped to the enum below
     * rather than cast, so an unrecognised direction fails here instead of reaching the API.
     */
    order?: Array<keyof typeof ListProviderPayersOrderEnum>;
}

export interface IPayerRoutingListing {
    items: ProviderPayer[];
    total: number;
}

export class PayerRoutingStore {
    @observable public loading: boolean = false;

    /** payerId → its clearinghouse connections. An ABSENT key means "not loaded yet". */
    @observable public bindingsByPayerId: Map<string, ProviderClearinghousePayer[]> = new Map();

    private inFlight: Set<string> = new Set();
    private features?: IFeatures;
    private generator?: PayerRoutingGenerator;

    constructor(features?: IFeatures, generator?: PayerRoutingGenerator) {
        this.features = features;
        this.generator = generator;
        makeObservable(this);
    }

    public getBindings(payerId: string | undefined): ProviderClearinghousePayer[] | undefined {
        return payerId ? this.bindingsByPayerId.get(payerId) : undefined;
    }

    /**
     * The payers reachable by a clearinghouse payer id.
     *
     * A payer has no payer id of its own. @John Russell, 2026-08-27: *"Payers dont have payer
     * ids. ClearinghousePayers do... a payer can have multiple payer ids. Its one to many."*
     * ENTER's naming convention puts ONE of them in the payer name, which is why searching the
     * name has worked so far. It stops working the moment a payer has two: Waystar issues a
     * separate paperwork id (J12345 against a claim id of 12345) that appears nowhere in the
     * name, so an operator holding the paperwork id finds nothing.
     *
     * No `filterActive` here, unlike the support equivalent. The provider request has no such
     * field and does not need one: the server already excludes disabled connections from this
     * endpoint, verified against production (see `loadForPayers`). Searching by an id that
     * belongs to a disabled connection therefore finds nothing, which is correct - that
     * connection is not carrying anything.
     *
     * Returns ids, not rows: the caller turns them into a `filterId` query against
     * `/provider/payer`, because the two cannot be one request (see `runSearch`).
     */
    public getPayerIdsByExternalId = async (externalId: string, limit: number = 50): Promise<string[]> => {
        if (this.features?.isDemo && this.generator) {
            return this.generator.getPayerIdsByExternalId(externalId, limit);
        }

        const rows = await CoreFetch<ClearinghousePayerApi>(ClearinghousePayerApi).listProviderClearinghousePayers({
            filterExternalId: [`LK:${externalId}`],
            limit,
        });

        return Array.from(new Set((rows || []).map(row => row.payer?.id).filter((id): id is string => !!id)));
    };

    /**
     * Create a payer from the provider console (FE-79).
     *
     * Sends the name exactly as typed. `POST /provider/payer` prepends "Generated Payer - " itself
     * (@John Russell, 2026-09-11: *"'Generated Payer' does not need to be added in the payload -
     * the backend will add it"*), and that prefix is what files the payer in ENTER Support's
     * Unconfigured payers queue. Once created, only Support can change it: the provider API has no
     * update, disable or delete.
     *
     * In demo mode the generator performs the same create in memory — the boundary would refuse
     * the POST, and "Not available in demo mode" is not what a prospect should see here.
     */
    public createPayer = async (payload: ProviderCreatePayerPayload): Promise<ProviderPayer> => {
        if (this.features?.isDemo && this.generator) {
            return this.generator.createPayer(payload);
        }

        return CoreFetch<PayerApi>(PayerApi).createProviderPayer({ providerCreatePayerPayload: payload });
    };

    /**
     * How many payers match a filter set, without pulling a page of them (FE-93).
     *
     * The tab counts come from the server's own answer — `limit: 1` and the pagination header — because counting the
     * rows that happen to be on screen would undercount by every payer past the first page (NS-3). In demo the
     * generator answers, as it does for the listing itself (NS-14).
     *
     * `undefined` when the server sends no usable `pagination-total` — missing, or not a whole number (see
     * `tabCountFromHeader`) — and deliberately not a number. The page fetched
     * one row, so falling back to its length reports "1" for a set of any size — a wrong count that looks like a real
     * one, on a tab an operator reads as the size of a queue (Copilot caught this on #537). A tab with no number is
     * honest about not knowing.
     */
    public countByFilters = async (filters: ICustomFilter[]): Promise<number | undefined> => {
        if (this.features?.isDemo && this.generator) {
            return this.generator.loadListing({ page: 0, pageSize: 1, filters }).total;
        }

        const raw = await CoreFetch<PayerApi>(PayerApi).listProviderPayersRaw({
            limit: 1,
            ...createRequestFilters(filters, true),
        });
        return tabCountFromHeader(raw.raw.headers.get('pagination-total'));
    };

    @action
    public loadListing = async (request?: IPayerRoutingListingRequest): Promise<IPayerRoutingListing> => {
        if (this.features?.isDemo && this.generator) {
            return this.generator.loadListing(request);
        }

        runInAction(() => {
            this.loading = true;
        });

        try {
            const limit = request?.pageSize ?? 25;
            const offset = request?.page && request?.pageSize ? request.page * request.pageSize : 0;
            const sort = request?.sort?.length ? request.sort : [ProviderPayerSortType.NAME];
            const order = (request?.order?.length ? request.order : ['ASCENDING']).map(
                direction => ListProviderPayersOrderEnum[direction],
            );
            const filters = request?.filters ? createRequestFilters(request.filters, true) : {};

            const raw = await CoreFetch<PayerApi>(PayerApi).listProviderPayersRaw({
                limit,
                offset,
                sort,
                order,
                ...filters,
            });

            const items = await raw.value();
            // The list length is only the page. `pagination-total` is the count pagination
            // needs, and the largest account has just over a thousand payers.
            const header = raw.raw.headers.get('pagination-total');
            const parsed = header ? parseInt(header, 10) : NaN;
            const total = Number.isNaN(parsed) ? items.length : parsed;

            runInAction(() => {
                this.loading = false;
            });

            return { items, total };
        } catch (error) {
            runInAction(() => {
                this.loading = false;
            });
            throw error;
        }
    };

    /**
     * Join clearinghouse connections for the payers currently on screen.
     *
     * Safe to call on every data update: ids already resolved or in flight are skipped.
     *
     * Disabled connections do not need filtering out here. `ProviderClearinghousePayer` has no
     * `active` field and the request has no `filterActive`, so the client could not ask even if
     * it wanted to — **verified against production 2026-08-25 that the server excludes them**:
     * of five disabled and five active connections, the disabled one was absent from this
     * endpoint's result. Without that check this join would have been an assumption about
     * routing rather than a fact.
     */
    @action
    public loadForPayers = async (payerIds: Array<string | undefined>): Promise<void> => {
        const pending = Array.from(
            new Set(
                payerIds.filter(
                    (id): id is string => !!id && !this.bindingsByPayerId.has(id) && !this.inFlight.has(id),
                ),
            ),
        );

        if (!pending.length) {
            return;
        }

        pending.forEach(id => this.inFlight.add(id));

        try {
            for (let i = 0; i < pending.length; i += MAX_IDS_PER_REQUEST) {
                await this.loadChunk(pending.slice(i, i + MAX_IDS_PER_REQUEST));
            }
        } catch (err) {
            /* tslint:disable-next-line:no-console */
            console.error('PayerRoutingStore: failed to load clearinghouse connections', err);
            // Deliberately leave the ids UNRESOLVED rather than caching an empty result. The
            // row then renders "unknown" instead of asserting that nothing is routed.
        } finally {
            pending.forEach(id => this.inFlight.delete(id));
        }
    };

    private async loadChunk(payerIds: string[]): Promise<void> {
        const bindings = await this.fetchByPayerIds(payerIds);

        const grouped = new Map<string, ProviderClearinghousePayer[]>();
        // Seed every id we asked about, so a payer with genuinely no connection resolves to a
        // known empty list — distinguishable from one nobody has asked about.
        payerIds.forEach(id => grouped.set(id, []));

        bindings.forEach(binding => {
            const payerId = binding.payer?.id;
            if (payerId && grouped.has(payerId)) {
                grouped.get(payerId)!.push(binding);
            }
        });

        runInAction(() => {
            grouped.forEach((rows, id) => this.bindingsByPayerId.set(id, rows));
        });
    }

    private async fetchByPayerIds(payerIds: string[]): Promise<ProviderClearinghousePayer[]> {
        if (this.features?.isDemo && this.generator) {
            return this.generator.fetchByPayerIds(payerIds);
        }

        return CoreFetch<ClearinghousePayerApi>(ClearinghousePayerApi).listProviderClearinghousePayers({
            filterPayerId: payerIds.map(id => `EQ:${id}`),
            limit: MAX_IDS_PER_REQUEST * 4,
        });
    }
}
